import React, { useCallback, useMemo } from 'react';

import { Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import Footer, { ClearButton } from '@adsk/alloy-react-footer';
import FormField from '@adsk/alloy-react-form-field';
import { useOnlyLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';
import ProgressRing from '@adsk/alloy-react-progress-ring';

import MultiSelectBox from './components/MultiSelectBox';
import SelectBox from './components/SelectBox';
import { SHEET_UNTAGGED } from './helpers/consts';
import { useTranslation } from './i18n';
import { useSheetFilter } from './providers/SheetFilterProvider';
import FiltersHeader from '../../FiltersHeader';

const SheetFilter = ({
  filters,
  hasFilters,
  floatingPanel,
  clearFilters,
  setFilters,
  onClose,
  open,
}) => {
  const { t } = useTranslation();
  const { locale } = useOnlyLocale();

  const {
    versionSets,
    tags,
    versionSetsLoading,
    versionSetsError,
    tagsLoading,
    tagsError,
    retryTags,
    retryVersionSets,
  } = useSheetFilter();
  const loading = versionSetsLoading && tagsLoading;

  const versionSetItems = useMemo(
    () =>
      versionSets.map((set) => ({
        value: set.id,
        label: set.name,
        subtext: t('VERSION_SET_DATE', {
          issuanceDate: set.issuanceDate
            ? displayDate(new Date(set.issuanceDate), {
                format: VALID_DATE_FORMATS.DEFAULT,
                locale,
              })
            : '',
        }),
      })),
    [versionSets, t, locale],
  );

  const tagItems = useMemo(
    () => [
      {
        value: SHEET_UNTAGGED,
        label: t('SHEET_UNTAGGED'),
      },
      ...tags.map((option) => ({
        value: option,
        label: option,
      })),
    ],
    [tags, t],
  );

  const selectedVersionSet = useMemo(
    () =>
      filters.setId
        ? versionSetItems.find(({ value }) => value === filters.setId)
        : null,
    [versionSetItems, filters.setId],
  );

  const selectedTags = useMemo(
    () =>
      Array.isArray(filters.tags) && filters.tags.length > 0
        ? tagItems.filter(({ value }) => filters.tags.includes(value))
        : [],
    [filters.tags, tagItems],
  );

  const getFilters = (filters) => {
    Object.keys(filters).forEach((key) => {
      const filter = filters[key];
      if (!filter || (Array.isArray(filter) && filter.length < 1)) {
        delete filters[key];
      }
    });
    return filters;
  };

  const setSelectedVersionSet = (selectedItem) => {
    setFilters(
      getFilters({
        ...filters,
        setId: selectedItem?.value,
      }),
    );
  };

  const setSelectedTags = (selectedItems) => {
    setFilters(
      getFilters({
        ...filters,
        tags: (selectedItems || []).map(({ value }) => value),
      }),
    );
  };

  const renderFooterError = (onRetry) => {
    return (
      <div>
        {t('ERROR_LOADING_RESULTS')}
        <LinkButton style={{ marginLeft: 4 }} onClick={onRetry}>
          {t('TRY_AGAIN')}
        </LinkButton>
      </div>
    );
  };

  const wrapFooterContent = useCallback((content) => {
    return (
      <Flex flex="1" justifyContent="center" alignItems="center">
        {content}
      </Flex>
    );
  }, []);

  const footerRenderer = ({ isLoading, hasError, onRetry, onClear }) => {
    let footerContent = null;

    if (isLoading) footerContent = wrapFooterContent(t('LOAD_MORE'));

    if (hasError) footerContent = wrapFooterContent(renderFooterError(onRetry));

    if (onClear) footerContent = <ClearButton onClick={onClear} />;

    return footerContent && <Footer>{footerContent}</Footer>;
  };

  const versionSetsFooterRenderer = () => {
    return footerRenderer({
      isLoading: versionSetsLoading,
      hasError: versionSetsError,
      onRetry: retryVersionSets,
      ...(selectedVersionSet && { onClear: () => setSelectedVersionSet(null) }),
    });
  };

  const tagsFooterRenderer = () => {
    return footerRenderer({
      isLoading: tagsLoading,
      hasError: tagsError,
      onRetry: retryTags,
      ...(selectedTags.length > 0 && { onClear: () => setSelectedTags([]) }),
    });
  };

  return (
    <Panel
      open={open}
      floating={floatingPanel}
      renderHeader={() => (
        <FiltersHeader
          floatingPanel={floatingPanel}
          onClose={onClose}
          title={t('FILTER_TITLE')}
          hasFilters={hasFilters}
          clearFilters={clearFilters}
        />
      )}
    >
      {loading ? (
        <Flex
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <ProgressRing size={ProgressRing.SIZES.MEDIUM} />
        </Flex>
      ) : (
        <Panel.Body style={{ overflow: 'auto' }}>
          <FormField
            label={t('VERSION_SET')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
            style={{ marginBottom: 16 }}
          >
            <SelectBox
              items={versionSetItems}
              selectedItem={selectedVersionSet}
              placeholder={t('FILTER_BY_VERSION_SET')}
              setSelectedItem={setSelectedVersionSet}
              renderFooter={versionSetsFooterRenderer}
            />
          </FormField>
          <FormField
            label={t('TAG')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
          >
            <MultiSelectBox
              items={tagItems}
              selectedItems={selectedTags}
              placeholder={selectedTags.length > 0 ? null : t('FILTER_BY_TAG')}
              setSelectedItems={setSelectedTags}
              renderFooter={tagsFooterRenderer}
            />
          </FormField>
        </Panel.Body>
      )}
    </Panel>
  );
};

export default SheetFilter;
